/** Read contract for the backend physical/member and current pricebook owners. */
export type HardwarePurchaseRow = {
  id: string; demandId: string; sourceEntityId: string | null; moduleId: string | null; ownerEntityId: string | null;
  hardwareClass: string; name: string; article: string; materialId: number | null;
  quantityDecimal: string; unit: string; resolved: boolean; refusalCode: string | null; priceRefusal: string | null;
  purchaseUnitPriceDecimal: string | null; purchaseSubtotalDecimal: string | null;
  clientUnitPriceDecimal: string | null; clientSubtotalDecimal: string | null;
  priceAuthority: Record<string, unknown> | null; physicalCatalog: Record<string, unknown>;
};
export type HardwarePurchaseProjection = {
  schema: 'holzcom.hardware-purchase-projection.v1'; modelId: string; tenantId: string | null;
  bundleVersion: number | null; bundleHash: string | null; sourceSha256: string;
  rows: HardwarePurchaseRow[]; physicalCohortSha256: string; contentSha256: string;
  pricingAuthority: Record<string, unknown>;
};
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const sha = (v: unknown) => typeof v === 'string' && /^[a-f0-9]{64}$/.test(v);
const decimal = (v: unknown) => typeof v === 'string' && /^(?:0|[1-9][0-9]{0,18})(?:\.[0-9]{1,6})?$/.test(v);
const nullableText = (v: unknown) => v === null || typeof v === 'string';

export function exactHardwarePurchase(value: unknown, modelId: string): HardwarePurchaseProjection {
  const fail = (): never => { throw new Error('HARDWARE_PURCHASE_RESPONSE_INVALID'); };
  if (!record(value) || value.schema !== 'holzcom.hardware-purchase-projection.v1' || value.modelId !== modelId
    || !nullableText(value.tenantId) || !nullableText(value.bundleHash)
    || !(value.bundleVersion === null || Number.isSafeInteger(value.bundleVersion) && Number(value.bundleVersion) >= 0)
    || !sha(value.sourceSha256) || !sha(value.physicalCohortSha256) || !sha(value.contentSha256)
    || !record(value.pricingAuthority) || !Array.isArray(value.rows)) return fail();
  const ids = new Set<string>();
  for (const row of value.rows) {
    if (!record(row) || typeof row.id !== 'string' || !row.id || ids.has(row.id)
      || typeof row.demandId !== 'string' || !row.demandId || typeof row.hardwareClass !== 'string' || !row.hardwareClass
      || typeof row.name !== 'string' || !row.name || typeof row.article !== 'string'
      || ![row.sourceEntityId, row.moduleId, row.ownerEntityId, row.refusalCode, row.priceRefusal].every(nullableText)
      || !(row.materialId === null || Number.isSafeInteger(row.materialId) && Number(row.materialId) > 0)
      || !decimal(row.quantityDecimal) || Number(row.quantityDecimal) <= 0
      || typeof row.unit !== 'string' || !row.unit || typeof row.resolved !== 'boolean' || !record(row.physicalCatalog)
      || row.resolved && (!row.article || row.refusalCode !== null) || !row.resolved && !row.refusalCode) return fail();
    ids.add(row.id);
    const money = [row.purchaseUnitPriceDecimal, row.purchaseSubtotalDecimal, row.clientUnitPriceDecimal, row.clientSubtotalDecimal];
    if (row.priceAuthority === null) {
      if (money.some(v => v !== null) || !row.priceRefusal) return fail();
    } else if (!record(row.priceAuthority) || row.priceAuthority.schema !== 'VIEWER_DB_PRICE_AUTHORITY.v1'
      || row.priceAuthority.currency !== 'RUB' || money.some(v => !decimal(v))
      || Number(row.purchaseUnitPriceDecimal) <= 0 || Number(row.clientUnitPriceDecimal) <= 0 || row.priceRefusal !== null
      || row.priceAuthority.pricebookId !== value.pricingAuthority.pricebookId
      || row.priceAuthority.pricebookSha256 !== value.pricingAuthority.pricebookSha256
      || row.priceAuthority.purchaseDecimal !== row.purchaseUnitPriceDecimal
      || row.priceAuthority.clientDecimal !== row.clientUnitPriceDecimal
      || !sha(row.priceAuthority.pricebookSha256) || !sha(row.priceAuthority.priceRowSha256)) return fail();
  }
  return value as HardwarePurchaseProjection;
}

/** Exact addition of backend decimal values; no price multiplication in the UI. */
export function addPurchaseDecimals(values: readonly string[]): string {
  const sum = values.reduce((total, value) => {
    if (!decimal(value)) throw new Error('HARDWARE_PURCHASE_DECIMAL_INVALID');
    const [whole, fraction = ''] = value.split('.');
    return total + BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0'));
  }, 0n);
  return `${sum / 1_000_000n}.${(sum % 1_000_000n).toString().padStart(6, '0')}`;
}
